import type {
  CompositionV2Filters,
  PostingFilters,
  SentimentV2Filters,
  TransitionsFiltersV2,
} from '@revelio/data-access';

import { type Filter, FilterType } from '../types/filters';
import { getNumericIdFromNestedId } from './get-numeric-id-from-nested-id';

type GeographyKeysBase = 'region' | 'country' | 'msa' | 'state' | 'city';
type GeographyKeysWithoutCitySupport = Exclude<GeographyKeysBase, 'city'>;

type GeographyFilterProperties =
  // Composition supports city
  Pick<CompositionV2Filters, GeographyKeysBase> &
    // Other domains do not yet support city; keep as partials
    Partial<Pick<PostingFilters, GeographyKeysWithoutCitySupport>> &
    Partial<Pick<SentimentV2Filters, GeographyKeysWithoutCitySupport>> &
    Partial<Pick<TransitionsFiltersV2, GeographyKeysWithoutCitySupport>>;

export const getV2GeographyFilters = (
  filter: Filter
): GeographyFilterProperties => {
  if (filter.type !== FilterType.TREE) return {};

  return filter.selections.reduce<GeographyFilterProperties>(
    (acc, selection) => {
      const selectionId = getNumericIdFromNestedId(String(selection.id));

      if (selectionId === null) {
        return acc;
      }

      if (selection.levelId === 'region') {
        return {
          ...acc,
          region: [...(acc.region ?? []), selectionId],
        };
      }
      if (selection.levelId === 'country') {
        return {
          ...acc,
          country: [...(acc.country ?? []), selectionId],
        };
      }
      if (selection.levelId === 'msa') {
        return {
          ...acc,
          msa: [...(acc.msa ?? []), selectionId],
        };
      }
      if (selection.levelId === 'state') {
        return {
          ...acc,
          state: [...(acc.state ?? []), selectionId],
        };
      }
      if (selection.levelId === 'city') {
        return {
          ...acc,
          city: [...(acc.city ?? []), selectionId],
        };
      }

      return acc;
    },
    {}
  );
};
